import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

export const SAVED_SEARCHES_KEY = 'ap_saved_searches';

export interface SavedSearch {
  id: string;
  label: string;
  params: Record<string, string>;
  savedAt: string;
}

function buildLabel(params: Record<string, string>): string {
  const parts: string[] = [];
  if (params.make) parts.push(params.make);
  if (params.model) parts.push(params.model);
  if (params.yearFrom) parts.push(`from ${params.yearFrom}`);
  if (params.yearTo) parts.push(`to ${params.yearTo}`);
  if (params.priceFrom || params.priceTo) {
    const range = [params.priceFrom && `€${Number(params.priceFrom).toLocaleString()}`, params.priceTo && `€${Number(params.priceTo).toLocaleString()}`]
      .filter(Boolean).join(' – ');
    parts.push(range);
  }
  if (params.fuelType) parts.push(params.fuelType);
  if (params.bodyType) parts.push(params.bodyType);
  if (params.location) parts.push(params.location);
  if (params.searchQuery) parts.push(`"${params.searchQuery}"`);
  return parts.length > 0 ? parts.join(' · ') : 'All vehicles';
}

const QUICK_SEARCHES: Array<{ label: string; icon: string; color: string; params: Record<string, string> }> = [
  { label: 'Sedans under €10k', icon: 'bi-car-front', color: '#17a2b8', params: { bodyType: 'sedan', priceTo: '10000' } },
  { label: 'SUVs & Off-Road', icon: 'bi-truck', color: '#fe5a1d', params: { bodyType: 'suv' } },
  { label: 'Electric Cars', icon: 'bi-lightning-charge', color: '#28a745', params: { fuelType: 'electric' } },
  { label: 'Under 50,000 km', icon: 'bi-speedometer2', color: '#6f42c1', params: { mileageTo: '50000' } },
];

export default function Mysearches() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searches, setSearches] = useState<SavedSearch[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SAVED_SEARCHES_KEY);
      setSearches(raw ? JSON.parse(raw) : []);
    } catch {
      setSearches([]);
    }
  }, []);

  const deleteSearch = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = searches.filter(s => s.id !== id);
    setSearches(updated);
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(updated));
  };

  const runSearch = (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    navigate(`/mysearch${qs ? `?${qs}` : ''}`);
  };

  return (
    <div className="dashboard-mysearches" style={{ paddingBottom: 40, maxWidth: 1000 }}>
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <h2 className="fw-bold mb-1">{t('my_searches.title', 'My Saved Searches')}</h2>
          <p className="text-muted mb-0">{t('my_searches.subtitle', 'Quickly access your favorite vehicle filters')}</p>
        </div>
        <button className="btn btn-primary shadow-sm" style={{ background: '#fe5a1d', border: 'none' }} onClick={() => navigate('/mysearch')}>
          <i className="bi bi-search me-2"></i> {t('saved_searches.new_search')}
        </button>
      </div>

      <div className="dash-panel shadow-sm mb-5 p-4">
        <h5 className="fw-bold mb-4 d-flex align-items-center">
          <div className="dash-stat-icon me-3" style={{ background: 'rgba(254,90,29,0.1)', color: 'var(--color1)', width: 36, height: 36, fontSize: 16 }}>
            <i className="bi bi-bookmark-heart"></i>
          </div>
          {t('saved_searches.your_saved')}
        </h5>

        {searches.length > 0 ? (
          <div className="row g-3">
            {searches.map(s => (
              <div key={s.id} className="col-md-6 col-lg-4">
                <div 
                  className="dash-stat-card shadow-sm h-100 p-3" 
                  style={{ cursor: 'pointer', transition: 'all 0.2s', border: '1px solid var(--dash-border)' }}
                  onClick={() => runSearch(s.params)}
                >
                  <div className="d-flex w-100 align-items-start gap-3">
                    <div className="dash-stat-icon" style={{ background: '#f1f5f9', color: 'var(--dash-text-muted)', width: 44, height: 44, fontSize: 16 }}>
                      <i className="bi bi-search"></i>
                    </div>
                    <div className="flex-grow-1 min-w-0">
                      <h6 className="fw-bold mb-1 text-truncate" style={{ fontSize: 14 }}>{s.label || buildLabel(s.params)}</h6>
                      <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                        Saved on {dayjs(s.savedAt).format('MMM D, YYYY')}
                      </p>
                    </div>
                    <button 
                      className="btn btn-sm btn-light text-danger p-0 d-flex align-items-center justify-content-center" 
                      style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0 }}
                      onClick={(e) => deleteSearch(s.id, e)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5 rounded-4" style={{ background: '#f8f9fa', border: '1px dashed #cbd5e1' }}>
            <i className="bi bi-bookmarks text-muted mb-3 d-block" style={{ fontSize: 40, opacity: 0.5 }}></i>
            <h6 className="fw-bold">{t('saved_searches.no_saved')}</h6>
            <p className="text-muted small mb-4" style={{ maxWidth: 300, margin: '0 auto' }}>
              {t('saved_searches.no_saved_hint')}
            </p>
            <button className="btn btn-outline-primary fw-bold" onClick={() => navigate('/mysearch')}>
              {t('saved_searches.browse_all')}
            </button>
          </div>
        )}
      </div>

      <div className="dash-panel shadow-sm p-4">
        <h5 className="fw-bold mb-4 d-flex align-items-center">
          <div className="dash-stat-icon me-3" style={{ background: 'rgba(23,162,184,0.1)', color: '#17a2b8', width: 36, height: 36, fontSize: 16 }}>
            <i className="bi bi-lightning-charge"></i>
          </div>
          {t('saved_searches.popular_quick')}
        </h5>

        <div className="row g-3">
          {QUICK_SEARCHES.map((qs, i) => (
            <div key={i} className="col-md-6 col-lg-3">
              <button 
                className="btn w-100 h-100 text-start p-3 dash-stat-card shadow-sm"
                style={{ background: '#fff', border: '1px solid var(--dash-border)', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '12px' }}
                onClick={() => runSearch(qs.params)}
              >
                <div className="dash-stat-icon" style={{ background: `${qs.color}15`, color: qs.color, width: 40, height: 40, fontSize: 18 }}>
                  <i className={`bi ${qs.icon}`} />
                </div>
                <h6 className="fw-bold mb-0" style={{ fontSize: 14 }}>{qs.label}</h6>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
